import { api } from '../../../services/api';
import type { PresignedImageUpload } from '../../uploads/types';
import type {
  SubscriptionTierRow,
  TierFeatures,
  TierCode,
} from '../../platform-admin/types/subscription-tiers';

export type ShelterStatus = 'active' | 'suspended';

/** Product subscription lifecycle (not org `ShelterStatus`). */
export type ShelterSubscriptionLifecycleStatus =
  | 'active'
  | 'cancelled'
  | 'paused'
  | 'pending';

export type MyShelterSubscription = {
  shelterId: string;
  subscriptionTierId: string | null;
  subscriptionTierName: string | null;
  subscriptionStatus: ShelterSubscriptionLifecycleStatus;
  subscriptionExpiresAt: string | null;
  subscriptionCancelledAt: string | null;
  subscriptionPausedAt: string | null;
  subscriptionEffectiveTo: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeSubscriptionStatus: string | null;
  stripeCurrentPeriodEnd: string | null;
  isPaidAndActive: boolean;
  requiresCheckout: boolean;
  hasStripeSubscription: boolean;
  updatedAt: string;
  idempotent?: boolean;
};

export type ShelterUsageCounts = {
  activeUsers: number;
  activeAnimals: number;
};

export type ResolvedShelterFeatures = {
  shelterId: string;
  subscriptionTierId: string | null;
  subscriptionTierName: string | null;
  tierCode: TierCode | null;
  subscriptionStatus: ShelterSubscriptionLifecycleStatus;
  isPaidAndActive: boolean;
  features: TierFeatures;
  usage: ShelterUsageCounts;
};

/** Populated on Super Admin `GET /shelters` list (computed + resolved). */
export type ShelterDirectoryMetrics = {
  animalCount: number;
  avgMatches: number | null;
  matchingRatePercent: number | null;
  visitsPerDay: number | null;
  trainingCompletionPercent: number | null;
};

export type Shelter = {
  _id: string;
  name: string;
  status: ShelterStatus;
  createdAt: string;
  updatedAt: string;
  shelterUrl?: string | null;
  shelterAdoptionFormUrl?: string | null;
  /** CC address for match-results share emails. */
  shelterMatchResultsCcEmail?: string | null;
  subscriptionTierId?: string;
  subscriptionTierName?: string;
  /** Free-text address or region for the directory. */
  location?: string | null;
  /** Dynamo user id (Cognito sub) of the designated product owner. */
  productOwnerUserId?: string | null;
  /** Denormalized or list-time resolved display string. */
  productOwnerDisplayName?: string | null;
  /** `YYYY-MM-DD` — MVP manual subscription end (not billing-backed). */
  subscriptionExpiresAt?: string | null;
  subscriptionStatus?: ShelterSubscriptionLifecycleStatus;
  stripeSubscriptionStatus?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionCancelledAt?: string | null;
  subscriptionPausedAt?: string | null;
  subscriptionEffectiveTo?: string | null;
  settings?: Record<string, unknown>;
  directoryMetrics?: ShelterDirectoryMetrics;
};

/** `settings.shelterLogo` — written by logo upload confirm. */
export type ShelterLogoSettings = {
  s3Key: string;
  contentType?: string;
  updatedAt?: string;
};

type ApiSuccess<T> = { success: boolean; data: T };

function unwrap<T>(res: { data: ApiSuccess<T> }): T {
  return res.data.data;
}

export async function listShelters(): Promise<{
  shelters: Shelter[];
  count: number;
}> {
  const res =
    await api.get<ApiSuccess<{ shelters: Shelter[]; count: number }>>(
      '/shelters',
    );
  return unwrap(res);
}

export async function getShelterById(id: string): Promise<Shelter> {
  const res = await api.get<ApiSuccess<Shelter>>(`/shelters/${id}`);
  return unwrap(res);
}

export async function createShelter(body: {
  name: string;
  adminInviteEmail: string;
  subscriptionTierId?: string;
  status?: ShelterStatus;
  shelterUrl?: string;
  shelterAdoptionFormUrl?: string;
  settings?: Record<string, unknown>;
}): Promise<Shelter> {
  const res = await api.post<ApiSuccess<Shelter>>('/shelters', body);
  return unwrap(res);
}

export async function updateShelter(
  id: string,
  body: Partial<{
    name: string;
    status: ShelterStatus;
    shelterUrl: string | null;
    shelterAdoptionFormUrl: string | null;
    shelterMatchResultsCcEmail: string | null;
    settings: Record<string, unknown> | null;
    location: string | null;
    productOwnerUserId: string | null;
    subscriptionExpiresAt: string | null;
    /** Super Admin — set or switch catalog tier (activates subscription row). */
    assignSubscriptionTierId: string;
    /** Super Admin — pause or cancel product subscription lifecycle. */
    subscriptionLifecycle: 'cancel' | 'pause';
  }>,
): Promise<Shelter> {
  const res = await api.patch<ApiSuccess<Shelter>>(`/shelters/${id}`, body);
  return unwrap(res);
}

export type EligibleShelterProductOwner = {
  userId: string;
  displayName: string;
  email: string;
  role: string;
};

/** Super Admin — users with no shelter (user | shelter_admin) for product owner assignment. */
export async function listEligibleShelterProductOwners(): Promise<{
  users: EligibleShelterProductOwner[];
}> {
  const res = await api.get<
    ApiSuccess<{ users: EligibleShelterProductOwner[] }>
  >('/admin/users/eligible-shelter-product-owners');
  return unwrap(res);
}

export async function deleteShelter(
  id: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await api.delete<ApiSuccess<{ deleted: boolean; id: string }>>(
    `/shelters/${id}`,
  );
  return unwrap(res);
}

/** Shelter Admin — catalog tiers for upgrade/downgrade picker. */
export async function listShelterAdminSubscriptionTiers(): Promise<{
  tiers: SubscriptionTierRow[];
}> {
  const res = await api.get<ApiSuccess<{ tiers: SubscriptionTierRow[] }>>(
    '/subscriptions/catalog',
  );
  return unwrap(res);
}

/** Shelter Admin — current subscription for the signed-in user's shelter only. */
export async function getMyShelterSubscription(): Promise<MyShelterSubscription> {
  const res = await api.get<ApiSuccess<MyShelterSubscription>>(
    '/shelters/me/subscription',
  );
  return unwrap(res);
}

/** Shelter staff — read-only limits and feature flags for the signed-in user's shelter. */
export async function getMyShelterFeatures(): Promise<ResolvedShelterFeatures> {
  const res = await api.get<ApiSuccess<ResolvedShelterFeatures>>(
    '/shelters/me/features',
  );
  return unwrap(res);
}

export async function patchMyShelterSubscriptionTier(body: {
  subscriptionTierId: string;
}): Promise<MyShelterSubscription> {
  const res = await api.patch<ApiSuccess<MyShelterSubscription>>(
    '/shelters/me/subscription/tier',
    body,
  );
  return unwrap(res);
}

export async function postMyShelterSubscriptionCancel(body?: {
  mode?: 'cancel' | 'pause';
}): Promise<MyShelterSubscription> {
  const res = await api.post<ApiSuccess<MyShelterSubscription>>(
    '/shelters/me/subscription/cancel',
    body ?? {},
  );
  return unwrap(res);
}

export type ShelterLogoViewUrlPayload = {
  signedUrl: string | null;
  expiresAt: string | null;
};

export async function postShelterLogoPresign(
  shelterId: string,
  body: { contentType: 'image/jpeg' | 'image/png'; fileSize?: number },
): Promise<PresignedImageUpload> {
  const res = await api.post<ApiSuccess<PresignedImageUpload>>(
    `/shelters/${shelterId}/logo/presign`,
    body,
  );
  return unwrap(res);
}

export async function postShelterLogoConfirm(
  shelterId: string,
  body: { key: string; contentType: 'image/jpeg' | 'image/png' },
): Promise<Shelter> {
  const res = await api.post<ApiSuccess<Shelter>>(
    `/shelters/${shelterId}/logo/confirm`,
    body,
  );
  return unwrap(res);
}

export async function deleteShelterLogo(shelterId: string): Promise<Shelter> {
  const res = await api.delete<ApiSuccess<Shelter>>(
    `/shelters/${shelterId}/logo`,
  );
  return unwrap(res);
}

export async function getShelterLogoViewUrl(
  shelterId: string,
): Promise<ShelterLogoViewUrlPayload> {
  const res = await api.get<ApiSuccess<ShelterLogoViewUrlPayload>>(
    `/shelters/${shelterId}/logo/view-url`,
  );
  return unwrap(res);
}
